import React, { useState, useEffect } from 'react';
import { Sparkles, Rocket, Clock, Star, Lock, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ComingSoonSection - Shows upcoming features on the dashboard
 * Currently hidden from regular users (super_admin only for preview)
 * Features are fetched from the API (managed via Admin Coming Soon page)
 */
const ComingSoonSection = ({ user }) => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Only show to super admins for now (as per user request)
  const isSuperAdmin = user?.roles?.includes('super_admin');
  
  useEffect(() => {
    if (isSuperAdmin) {
      fetchFeatures();
    }
  }, [isSuperAdmin]);
  
  const fetchFeatures = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/coming-soon/features`,
        { withCredentials: true }
      );
      setFeatures(response.data.features || []);
    } catch (error) {
      console.error('Failed to fetch coming soon features:', error);
      // Fallback to default features on error
      setFeatures([
        { title: "AI Interview Coach", description: "Practice interviews with AI-powered feedback", icon: "🎯", eta: "Q1 2026", category: "premium" },
        { title: "Job Application Tracker", description: "Track all your applications in one place", icon: "📊", eta: "Q1 2026", category: "free" },
        { title: "Salary Negotiation Guide", description: "AI-powered salary research and scripts", icon: "💰", eta: "Q2 2026", category: "premium" },
        { title: "Skills Assessment", description: "Identify skill gaps and get learning paths", icon: "🧠", eta: "Q2 2026", category: "free" },
        { title: "Company Research Tool", description: "Deep dive into company culture and reviews", icon: "🔍", eta: "Q2 2026", category: "premium" },
        { title: "Networking Hub", description: "Connect with professionals in your industry", icon: "🤝", eta: "Q3 2026", category: "premium" }
      ]);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isSuperAdmin) {
    return null;
  }
  
  if (loading) {
    return (
      <div className="mb-8 flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
      </div>
    );
  }
  
  if (features.length === 0) {
    return null;
  }
  
  return (
    <div className="mb-8">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
            <Rocket className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Coming Soon</h2>
            <p className="text-sm text-gray-500">Exciting features in development</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
          Preview (Admin Only)
        </span>
      </div>
      
      {/* Feature Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {features.map((feature, index) => (
          <div
            key={feature.id || index}
            className="relative bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all group"
          >
            {/* Coming Soon Badge */}
            <div className="absolute -top-2 -right-2 flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full shadow-lg">
              <Clock className="h-3 w-3" />
              {feature.eta}
            </div>
            
            {/* Category Badge */}
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mb-3 ${
              feature.category === 'premium' 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-green-100 text-green-700'
            }`}>
              {feature.category === 'premium' ? <Star className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
              {feature.category}
            </div>
            
            {/* Feature Info */}
            <div className="flex items-start gap-3">
              <span className="text-2xl">{feature.icon}</span>
              <div>
                <h3 className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {feature.description}
                </p>
              </div>
            </div>
            
            {/* Lock Overlay */}
            <div className="absolute inset-0 bg-gray-900/5 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white/90 rounded-full shadow-sm">
                <Lock className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">In Development</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Info Note */}
      <p className="text-center text-sm text-gray-500 mt-4">
        💡 Have a feature request? Contact us at <a href="mailto:support@careersupport.io" className="text-purple-600 hover:underline">support@careersupport.io</a>
      </p>
    </div>
  );
};

export default ComingSoonSection;
